import React, { useMemo } from "react";
import styled from "@emotion/styled";
import Radio from "../../../components/forms/Radio";
import {
  QuestionWithRelations,
  QuestionOptionWithRelations,
} from "../../../../../api/src/types";
import { useGetCurrentUserQuery } from "../../../api/users";
import { useUpsertAnswerMutation } from "../../../api/answers";
import { useGetQuestionOptionVisibilityQuery } from "../../../api/questionnaire";
import {
  /*IconContainer, */ ErrorWrapper,
  InputWrapper,
} from "./SharedFormStyles";
import useIsMobile from "../../../hooks/useIsMobile";
import Loader from "../../../components/icons/Loader";

export const RadioContainer = styled.div`
  flex: 1;
`;

interface RadioInputProps {
  question: QuestionWithRelations;
  documentId: string;
}

const RadioInput: React.FC<RadioInputProps> = ({ question, documentId }) => {
  const isMobile = useIsMobile();
  const { data: user } = useGetCurrentUserQuery();

  const [
    upsertAnswer,
    {
      /*isLoading: isUpserting, */ isError: isUpsertError,
      reset: resetUpdateAnswer,
    },
  ] = useUpsertAnswerMutation();

  const hasError = isUpsertError;

  const selectedOptionId = useMemo(
    () =>
      question.answers[0]?.answerQuestionOptions.map(
        (a) => a.questionOptionId,
      )[0] ?? null,
    [question.answers],
  );

  // get the ids of the options that are restricted
  const restrictedOptionIds = question.questionOptions
    .filter((option) =>
      option.restrictedQuestionOptions.some(
        (o) => o.restrictedQuestionOptionId === option.id,
      ),
    )
    .map((option) => option.id);

  // get the visibility status of the restricted options
  const { data: visibilityData, isLoading: isVisibilityLoading } =
    useGetQuestionOptionVisibilityQuery(
      {
        restrictedQuestionOptionIds: restrictedOptionIds,
        userId: user?.id ?? "",
      },
      {
        skip: !user?.id || restrictedOptionIds.length === 0,
      },
    );

  const save = (optionId: string) => {
    resetUpdateAnswer();

    if (!user) return;
    // setPendingId(optionId);

    const payload = {
      questionId: question.id,
      userId: user.id,
      documentId,
      rootQuestionId: question.rootQuestionId ?? question.id,
      sectionId: question.parentQuestionnaireSectionId,
      questionOptionIds: [optionId],
      serializeDebounceMs: 300,
    };

    void upsertAnswer(payload);
  };

  // Handle radio selection
  const handleRadioChange = (optionId: string) => {
    save(optionId);
  };

  return (
    <>
      {question.questionOptions.map((option: QuestionOptionWithRelations) => {
        // const isLoading = pendingId === option.id && (isCreating || isUpdating);
        const isRestricted = restrictedOptionIds.includes(option.id);
        const isVisible = isRestricted
          ? isVisibilityLoading
            ? "loading"
            : (visibilityData?.[option.id] ?? false)
          : true;

        if (isVisible === "loading") {
          return (
            <InputWrapper key={option.id}>
              <RadioContainer>
                <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
              </RadioContainer>
            </InputWrapper>
          );
        }

        if (!isVisible) return null;

        return (
          <React.Fragment key={option.id}>
            <InputWrapper>
              <RadioContainer>
                <Radio
                  label={option.value || ""}
                  id={option.id}
                  isChecked={selectedOptionId === option.id}
                  onToggle={() => {
                    handleRadioChange(option.id);
                  }}
                />
              </RadioContainer>
              {/* Commenting out the loader for now as per the request of the design team */}
              {/* {isLoading && (
                <IconContainer>
                  <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
                </IconContainer>
              )} */}
            </InputWrapper>
          </React.Fragment>
        );
      })}
      {hasError && (
        <ErrorWrapper>
          Error saving answer, please refresh the page and try again
        </ErrorWrapper>
      )}
    </>
  );
};

export default RadioInput;
